// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';
import {VNode} from 'snabbdom';
import {selx} from 'clsx';

import {SvgIconEl} from 'common/components/svg-icon';
import {ExperimentalTooltipEl} from 'common/components/tooltip';
import {ContextPanelMenuEl} from 'common/widgets/context-panel/context-panel-menu';
import {ButtonEl} from 'common/components/button';
import {DropMenuEl, ExperimentalDropMenuEl} from 'common/components/drop-menu';
import {Directions, PositioningStrategy} from 'common/components/drop-menu/types';
import {InputEl} from 'common/components/input';
import {ItemsMenuEl} from 'common/components/items-menu';
import {Placement} from 'common/components/tooltip/types';
import {ButtonSize, ButtonTheme, ButtonType} from 'common/components/button/constants';

import {MarkdownEl} from '../markdown';
import {ButtonBarEl} from '../button-bar';
import type {SelectDropdown} from './index';
import {SelectTheme} from './constants';
import styles from './index.module.styl';

const Label = ({
  includeLeftIcon,
  includeRightIcon,
  hasSelectedLabel,
  isOverride,
  selectLabel,
  selectLabelTitle,
  leftIcon,
  leftIconColor,
  rightIcon,
  rightIconColor,
  hasSlot,
  shortcutLabel,
}: {
  includeLeftIcon: boolean;
  includeRightIcon: boolean;
  hasSelectedLabel: boolean;
  isOverride: boolean;
  selectLabel: string;
  selectLabelTitle: string;
  leftIcon: string;
  leftIconColor?: string;
  shortcutLabel?: string;
  rightIcon: string;
  rightIconColor?: string;
  hasSlot: boolean;
}) => {
  const hasButtonSuffixSlot = hasSlot;
  const isCaretIcon = (icon: string) => icon.startsWith(`caret-`);
  return (
    <div sel={selx(styles.selectLabelWrapper)} attrs={{title: selectLabelTitle}}>
      {leftIcon && includeLeftIcon ? (
        <SvgIconEl
          sel={selx(styles.icon, {
            [`${styles.caretIcon}`]: isCaretIcon(leftIcon),
          })}
          attrs={{
            elref: `leftIcon`,
            icon: leftIcon,
            color: leftIconColor,
          }}
          class={{[styles.selectHasLabel]: hasSelectedLabel}}
        />
      ) : null}
      {hasSelectedLabel &&
        (isOverride ? (
          <MarkdownEl
            sel={selx(styles.selectLabel)}
            attrs={{text: selectLabel, elref: `label`, inline: true, truncate: true}}
          />
        ) : (
          <div sel={selx(styles.selectLabel)} attrs={{elref: `label`}}>
            {selectLabel}
          </div>
        ))}
      {hasButtonSuffixSlot ? <slot attrs={{name: `button-suffix`}} /> : null}
      {rightIcon && includeRightIcon ? (
        <SvgIconEl
          sel={selx(styles.icon, {
            [`${styles.caretIcon}`]: isCaretIcon(rightIcon),
          })}
          attrs={{
            elref: `rightIcon`,
            icon: rightIcon,
            color: rightIconColor,
          }}
          class={{[styles.selectHasLabel]: hasSelectedLabel}}
        />
      ) : null}
      {shortcutLabel && <span sel={selx(styles.shortcutLabel)}>{shortcutLabel}</span>}
    </div>
  );
};

function SelectTriggerButton({selectCmp}: {selectCmp: SelectDropdown}): VNode {
  const hasTriggerSlot = selectCmp.helpers.hasSlot(`trigger`);
  const {isOpen} = selectCmp.state;
  const theme = selectCmp.helpers.getTriggerButtonTheme();
  const hasSelectedLabel = selectCmp.helpers.hasSelectedLabel();
  const selectLabel = selectCmp.helpers.selectedLabel();
  const selectLabelTitle = selectCmp.helpers.getTitle();
  const {leftIcon, leftIconColor, rightIcon, rightIconColor} = selectCmp.helpers.getIcon();
  const hasSlot = selectCmp.helpers.hasSlot(`button-suffix`);

  return (
    <div
      sel={selx(styles.selectTrigger)}
      attrs={{
        elref: `trigger`,
      }}
      class={{
        [styles.hasTriggerSlot]: hasTriggerSlot,
      }}
      on={{
        click: selectCmp.helpers.isDropMenuDisabled() ? null : () => selectCmp.setDropMenuIsOpen(!isOpen),
        mouseenter: selectCmp.helpers.handleTriggerMouseEnter,
        mouseleave: selectCmp.helpers.handleTriggerMouseLeave,
      }}
    >
      {(() => {
        if (selectCmp.helpers.isButtonSelect()) {
          return (
            <ButtonEl
              sel={selx(styles.selectTriggerButton)}
              class={{
                truncated: !selectCmp.attr(`no-button-truncate`),
              }}
              attrs={{
                theme,
                type: selectCmp.attr(`theme`) === SelectTheme.WarningActions ? ButtonType.Warn : ButtonType.Info,
                size: selectCmp.attr(`size`),
                disabled: selectCmp.attr(`disabled`),
                'disable-pressed-styles': selectCmp.params.disablePressedStyles,
                'disabled-stays-opaque': selectCmp.attr(`disabled-stays-opaque`),
                readonly: selectCmp.attr(`readonly`),
                layout: selectCmp.attr(`layout`),
                'not-focusable': hasTriggerSlot,
                pressed: isOpen,
                ...(leftIcon ? {icon: leftIcon} : {}),
                ...(leftIconColor ? {'icon-color': leftIconColor} : {}),
                'no-rounded-corners': selectCmp.attr(`no-rounded-corners`),
                'experimental-positioning': selectCmp.attr(`experimental-positioning`),
              }}
              on={{
                focus: !hasTriggerSlot ? selectCmp.handleFocus : null,
                blur: !hasTriggerSlot ? selectCmp.handleBlur : null,
              }}
            >
              {!selectCmp.attr(`icon-only`) || selectCmp.attr(`show-icon-only-down-caret`) ? (
                <Label
                  includeLeftIcon={false}
                  includeRightIcon={selectCmp.helpers.shouldShowRightIcon()}
                  hasSelectedLabel={hasSelectedLabel}
                  isOverride={selectCmp.attr(`label`) !== null}
                  selectLabel={selectLabel}
                  selectLabelTitle={selectLabelTitle}
                  leftIcon={leftIcon}
                  leftIconColor={leftIconColor}
                  rightIcon={rightIcon}
                  rightIconColor={rightIconColor}
                  shortcutLabel={selectCmp.attr(`shortcut-label`)}
                  hasSlot={hasSlot}
                />
              ) : null}
              {selectCmp.showClearButton() ? (
                <ButtonEl
                  sel={selx(styles.clearButton)}
                  attrs={{
                    elref: `clearButton`,
                    theme: ButtonTheme.Lightweight,
                    type: ButtonType.Destructive,
                    size: ButtonSize.SmallIcon,
                    icon: `circle-clear`,
                  }}
                  on={{click: selectCmp.helpers.handleClearClick}}
                />
              ) : null}
            </ButtonEl>
          );
        } else if (hasTriggerSlot) {
          return <slot sel={selx(styles.triggerSlot)} attrs={{name: `trigger`}} />;
        }
        return (
          <Label
            includeLeftIcon
            includeRightIcon={selectCmp.helpers.shouldShowRightIcon()}
            hasSelectedLabel={hasSelectedLabel}
            isOverride={selectCmp.attr(`label`) !== null}
            selectLabel={selectLabel}
            selectLabelTitle={selectLabelTitle}
            leftIcon={leftIcon}
            leftIconColor={leftIconColor}
            rightIcon={rightIcon}
            rightIconColor={rightIconColor}
            shortcutLabel={selectCmp.attr(`shortcut-label`)}
            hasSlot={hasSlot}
          />
        );
      })()}
    </div>
  );
}

const DropdownBody = ({selectCmp}: {selectCmp: SelectDropdown}): VNode => {
  const {searchFilter, sectionsFromInputFn, dropdownBodyRenderer} = selectCmp.state;
  const hasBodySlot = selectCmp.helpers.hasSlot(`body`);
  const isSearchable = selectCmp.helpers.isSearchable();
  const menuWidthStyle = selectCmp.helpers.getDropMenuWidthStyle();
  const menuHeightStyle = selectCmp.helpers.getDropMenuHeightStyle();
  const menuPaddingStyle = selectCmp.helpers.getDropMenuPaddingStyle();
  if (dropdownBodyRenderer) {
    return dropdownBodyRenderer();
  }
  if (hasBodySlot) {
    return <slot attrs={{name: `body`}} />;
  }
  return (
    <>
      {selectCmp.attr(`header-title`) ? (
        <div
          attrs={{
            elref: `headerTitle`,
          }}
          sel={selx(styles.headerTitle)}
          style={menuWidthStyle}
        >
          {selectCmp.attr(`back-button`) ? (
            <ButtonEl
              sel={selx(styles.backButton)}
              attrs={{theme: ButtonTheme.Tertiary, icon: `arrow-left`, title: `Back (⇦ Left Arrow)`}}
              on={{click: selectCmp.helpers.handleBackClick}}
            />
          ) : null}
          <div sel={selx(styles.titleText)}>{selectCmp.attr(`header-title`)}</div>
        </div>
      ) : null}
      {isSearchable ? (
        <InputEl
          attrs={{
            elref: `searchInput`,
          }}
          key={selectCmp.attr(`header-title`)}
          sel={selx(styles.searchInput)}
          style={menuWidthStyle}
          params={{
            hideFocusStyling: selectCmp.attr(`hide-input-focus-styling`),
            icon: `search`,
            placeholder: selectCmp.attr(`search-placeholder`),
            showClear: true,
            onClear: () => selectCmp.helpers.handleSearchInput(``),
            onInput: (v) => selectCmp.helpers.handleSearchInput(v),
          }}
          props={{value: searchFilter}}
          hook={{
            insert: selectCmp.helpers.handleSearchInputInsert,
            destroy: () => selectCmp.focus(),
          }}
        />
      ) : null}
      <ItemsMenuEl
        key={selectCmp.attr(`header-title`)}
        style={{
          ...menuWidthStyle,
          ...menuHeightStyle,
          ...menuPaddingStyle,
        }}
        attrs={{
          elref: `selectItemsMenu`,
          tabindex: 0,
          multiple: selectCmp.attr(`multiple`),
          'show-select-all': selectCmp.attr(`show-select-all`),
          'search-filter': searchFilter,
          'empty-message': selectCmp.attr(`empty-message`),
          'align-all-text': selectCmp.attr(`align-all-text`),
          'disable-first-item-padding': selectCmp.attr(`disable-first-item-padding`),
          theme: selectCmp.attr(`list-theme`),
          'show-section-label-matches': selectCmp.attr(`show-section-label-matches`),
        }}
        props={{
          sections: selectCmp.helpers.getItemsMenuSections(),
          sectionsFromInputFn,
          keydownProxyEl: selectCmp,
          contextPanelTransform: selectCmp.helpers.itemContextPanelTransform(),
          contextPanelPositionParentElement: selectCmp.contextPanelPositionParentElement,
        }}
        on={{
          clickItem: selectCmp.helpers.handleClickItem,
          focusItem: selectCmp.helpers.handleFocusItem,
          selectAll: selectCmp.helpers.handleSelectAll,
        }}
      >
        {selectCmp.helpers.hasSlot(`context-panel-content`) && (
          <slot attrs={{name: `context-panel-content`, slot: `context-panel-content`}} />
        )}
      </ItemsMenuEl>
      {selectCmp.attr(`multiple-save-button`) ? (
        <ButtonBarEl on={{click: selectCmp.helpers.handleSaveClicked}}>Save</ButtonBarEl>
      ) : null}
    </>
  );
};

export function Template(this: SelectDropdown) {
  const {isOpen, tooltipData} = this.state;
  const hasTriggerSlot = this.helpers.hasSlot(`trigger`);
  const hasFooterSlot = this.helpers.hasSlot(`footer`);
  const tooltip = this.helpers.getTooltip();

  /** dynamically sets to render the dropdown and tooltip elements at the document.body level instead of within this select component */
  const DropdownEl = this.attr(`experimental-positioning`) ? ExperimentalDropMenuEl : DropMenuEl;

  return (
    <div sel={selx(styles.selectContainer)} class={{[styles.hasTriggerSlot]: hasTriggerSlot}}>
      {!isOpen && tooltip && (
        <ExperimentalTooltipEl
          attrs={{
            placement: Placement.Top,
            'delay-in': 10,
            'delay-out': 10,
            ...tooltip,
          }}
          params={{text: tooltip.text}}
          props={{parentEl: this}}
        />
      )}
      {!isOpen && this.helpers.hasHoverContextPanel() && tooltipData ? (
        <ContextPanelMenuEl
          attrs={{
            elref: `hoverContextPanel`,
            placement: tooltipData.placement || Directions.Down,
            'position-strategy': PositioningStrategy.Fixed,
          }}
          params={{
            data: tooltipData,
          }}
          on={{
            contextPanelHoverIn: this.helpers.handleContextPanelHoverIn,
            contextPanelHoverOut: this.helpers.handleContextPanelHoverOut,
          }}
        />
      ) : null}
      <SelectTriggerButton selectCmp={this} />
      {/*putting if block here to conserve dom memory and only load options list until opened*/}
      {isOpen
        ? (() => {
            return (
              <div sel={selx(styles.selectDropMenu)} attrs={{elref: `selectDropMenuContainer`}}>
                <DropdownEl
                  attrs={{
                    'data-force-visibility': this.attr(`force-visibility`),
                    elRef: `selectDropMenu`,
                    open: isOpen,
                    direction: this.attr(`menu-alignment`),
                    'experimental-positioning': this.attr(`experimental-positioning`),
                    'prevent-close': this.attr(`prevent-close`),
                    'prevent-close-selectors': [
                      `.create-custom-button`,
                      `mp-tooltip`, // prevent clicks on tooltips from closing the menu, allows users to click links within tooltips
                    ],
                    ...(this.attr(`offset`) ? {offset: this.attr(`offset`)} : {}),
                  }}
                  on={{
                    change: (ev: CustomEvent) => this.helpers.handleDropMenuChange(ev),
                    mouseenter: this.helpers.handleMenuMouseEnter,
                    mouseleave: this.helpers.handleMenuMouseLeave,
                  }}
                  hook={{insert: this.helpers.handleDropMenuInsert}}
                  props={{positionParent: this.dropdownPositionParent ?? this}}
                >
                  <DropdownBody selectCmp={this} />
                  {/*footer slot*/}
                  {hasFooterSlot ? (
                    <div sel={selx(`select-footer`)}>
                      <slot attrs={{name: `footer`}} />
                    </div>
                  ) : null}
                </DropdownEl>
              </div>
            );
          })()
        : null}
    </div>
  );
}
